import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16 } from '@onefootprint/icons';
import { Select } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import useDocumentText from '@/entity/hooks/use-document-text';
import type { TransformedDocument } from '@/entity/hooks/use-documents';
import { getUploadIdentifierFromKey, getUploadKey } from '../../utils/get-doc-upload-key';
type DocumentDialogTitleProps = {
  document: TransformedDocument;
  customUploadKey: string;
  onChangeUploadKey: (uploadKey: string) => void;
};

// This component is the dialog title for DocumentDetailsDialog:
// 1) Non-custom document: returns the document kind name
// 2) Custom document with > 1 upload: returns a Select to choose which upload is shown in the dialog
// 3) Custom document with 1 upload: returns that upload identifier
const DocumentDialogTitle = t0 => {
  const $ = _c(22);
  const {
    document,
    customUploadKey,
    onChangeUploadKey
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset.documents.details"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const getDocText = useDocumentText();
  let t2;
  if ($[1] !== customUploadKey) {
    t2 = getUploadIdentifierFromKey(customUploadKey);
    $[1] = customUploadKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const valueIdentifier = t2;
  if (document.kind !== "custom") {
    let t3;
    if ($[3] !== document.kind || $[4] !== getDocText) {
      t3 = getDocText(document.kind);
      $[3] = document.kind;
      $[4] = getDocText;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (document.uploads.length === 1) {
    return document.uploads[0].identifier;
  }
  let t3;
  if ($[6] !== document.uploads || $[7] !== t) {
    let t4;
    if ($[9] !== t) {
      t4 = upload => {
        const isSuccessful = upload.failureReasons.length === 0;
        return {
          component: <div className="flex items-center gap-1"><span className={cx("text-body-2", {
              "text-success": isSuccessful,
              "text-error": !isSuccessful
            })}>{isSuccessful ? t(_temp) : t(_temp2)}</span><span className="text-label-2">⋅</span><span className="text-body-2 text-secondary">{format(new Date(upload.timestamp), "MM/dd/yy, h:mma")}</span></div>,
          label: getUploadKey(upload),
          value: getUploadKey(upload)
        };
      };
      $[9] = t;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = document.uploads.map(t4);
    $[6] = document.uploads;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const options = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "popper",
      side: "bottom",
      align: "center",
      sideOffset: 2
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== valueIdentifier) {
    t5 = <span className="truncate">{valueIdentifier}</span>;
    $[12] = valueIdentifier;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IcoChevronDown16 />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5) {
    t7 = <div className="flex items-center gap-1">{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== customUploadKey || $[18] !== onChangeUploadKey || $[19] !== options || $[20] !== t7) {
    t8 = <Select className="text-label-2 text-primary" contentProps={t4} customTrigger={t7} options={options} value={customUploadKey} onChange={onChangeUploadKey} />;
    $[17] = customUploadKey;
    $[18] = onChangeUploadKey;
    $[19] = options;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
export default DocumentDialogTitle;
function _temp($) {
  return $.status.success;
}
function _temp2($_0) {
  return $_0.status.failed;
}